import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { Volume2, Sticker, Boxes, Play } from "lucide-react";
import { toast } from "sonner";

export const ScratchPicker = ({ open, onOpenChange, onPickAsset }) => {
  const [data, setData] = useState({ stickers: [], sounds: [], projects: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/scratch/assets").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const playSound = (url) => {
    try { new Audio(url).play(); } catch { toast.error("Couldn't preview"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl border-black/10" data-testid="scratch-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">Scratch · graphics & sound</DialogTitle>
          <DialogDescription className="text-xs uppercase tracking-[0.2em] text-[#E60000] font-bold">
            CC0 assets via scratch.mit.edu · drop into your edits
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="stickers">
          <TabsList className="rounded-sm h-10 bg-transparent border border-black/15 p-0">
            <TabsTrigger value="stickers" data-testid="scratch-tab-stickers" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white px-5"><Sticker className="w-3.5 h-3.5 mr-2" /> Stickers</TabsTrigger>
            <TabsTrigger value="sounds" data-testid="scratch-tab-sounds" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white px-5"><Volume2 className="w-3.5 h-3.5 mr-2" /> Sounds</TabsTrigger>
            <TabsTrigger value="projects" data-testid="scratch-tab-projects" className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white px-5"><Boxes className="w-3.5 h-3.5 mr-2" /> Projects</TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="py-12 text-center text-sm text-black/40">Loading Scratch library…</div>
          ) : (
            <>
              <TabsContent value="stickers" className="mt-4">
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-3" data-testid="scratch-stickers-grid">
                  {data.stickers.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => { onPickAsset?.({ type: "sticker", ...s }); toast.success(`${s.name} ready`); }}
                      className="aspect-square border border-black/10 hover:border-[#E60000] p-3 flex flex-col items-center justify-center group bg-white"
                      data-testid={`scratch-sticker-${i}`}
                    >
                      <img
                        src={s.url}
                        alt={s.name}
                        className="w-12 h-12 object-contain group-hover:scale-110 transition-transform"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                      <span className="text-[10px] mt-1 text-black/60 truncate w-full text-center">{s.name}</span>
                    </button>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="sounds" className="mt-4">
                <div className="space-y-2" data-testid="scratch-sounds-list">
                  {data.sounds.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 border border-black/10 hover:border-black px-4 py-3" data-testid={`scratch-sound-${i}`}>
                      <Volume2 className="w-4 h-4 text-[#E60000]" />
                      <span className="flex-1 text-sm font-medium tracking-tight">{s.name}</span>
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => playSound(s.url)} data-testid={`scratch-sound-play-${i}`}>
                        <Play className="w-3.5 h-3.5 mr-1" /> Preview
                      </Button>
                      <Button size="sm" className="rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white" onClick={() => onPickAsset?.({ type: "sound", ...s })} data-testid={`scratch-sound-pick-${i}`}>
                        Use
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="projects" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="scratch-projects-grid">
                  {data.projects.map((p, i) => (
                    <a
                      key={p.id}
                      href={`https://scratch.mit.edu/projects/${p.id}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border border-black/10 hover:border-[#E60000] group flex flex-col"
                      data-testid={`scratch-project-${p.id}`}
                    >
                      <div className="aspect-video relative bg-gradient-to-br from-[#E60000] to-black overflow-hidden">
                        <div className="absolute inset-0 grain opacity-40" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Play className="w-12 h-12 text-white/90 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                        </div>
                        <span className="absolute top-2 left-2 font-mono text-[10px] tracking-wider px-1.5 py-0.5 bg-white text-black font-bold">
                          SCRATCH
                        </span>
                      </div>
                      <div className="p-3 bg-white">
                        <div className="font-heading font-bold text-sm">{p.name}</div>
                        <div className="font-mono text-[10px] text-black/40 group-hover:text-[#E60000]">
                          scratch.mit.edu/projects/{p.id} ↗
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
